/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { Modal, ModalFuncProps } from "@duxton-web/ui"

type AnyMutationHook<TArg, TResult, TMutationState> = (
  options?: any,
) => readonly [(arg: TArg) => Promise<TResult>, TMutationState]

/**
 * Wraps an RTK Query mutation hook with a Modal.confirm gate.
 *
 * The mutation is only triggered after the user confirms — so `isLoading`
 * stays false while the dialog is open, matching the behaviour of the old
 * showConfirmMiddleware.
 *
 * @example
 * const [deleteItem, { isLoading }] = useConfirmedMutation(
 *   useDeleteItemMutation,
 *   { title: "Delete item?", content: "This cannot be undone." },
 * )
 *
 * <Button loading={isLoading} onClick={() => deleteItem({ merchantID, itemID })} />
 */
const useConfirmedMutation = <TArg, TResult, TMutationState>(
  useMutationHook: AnyMutationHook<TArg, TResult, TMutationState>,
  confirmOptions?: ModalFuncProps,
): readonly [(arg: TArg) => Promise<TResult | null>, TMutationState] => {
  const [trigger, result] = useMutationHook()

  const triggerWithConfirm = (arg: TArg): Promise<TResult | null> =>
    new Promise(resolve => {
      Modal.confirm({
        title: "Confirm",
        content: "Are you sure?",
        ...confirmOptions,
        onOk: () => resolve(trigger(arg)),
        onCancel: () => resolve(null),
      })
    })

  return [triggerWithConfirm, result] as const
}

export default useConfirmedMutation
